import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { submitDaily } from "@/api/burnoff";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";

const dailySchema = z.object({
  energy: z.number().min(1).max(5),
});

const TEAM_SLUG = "kutsa";

function getDateKey() {
  return new Date().toISOString().slice(0, 10);
}

function getStorageKey(slug, dateKey) {
  return `burnoff_daily_${slug}_${dateKey}`;
}

function getTodayLabel() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

const SCALE = [1, 2, 3, 4, 5];

const ENERGY_BG = {
  1: "bg-[hsl(var(--energy-1))]",
  2: "bg-[hsl(var(--energy-2))]",
  3: "bg-[hsl(var(--energy-3))]",
  4: "bg-[hsl(var(--energy-4))]",
  5: "bg-[hsl(var(--energy-5))]",
};
const ENERGY_BG_SELECTED = {
  1: "bg-[hsl(var(--energy-1-selected))]",
  2: "bg-[hsl(var(--energy-2-selected))]",
  3: "bg-[hsl(var(--energy-3-selected))]",
  4: "bg-[hsl(var(--energy-4-selected))]",
  5: "bg-[hsl(var(--energy-5-selected))]",
};
const ENERGY_TEXT_SELECTED = "text-white";
const ENERGY_TEXT_UNSELECTED = "text-stone-700";

export default function DailyPage() {
  const dateKey = getDateKey();
  const storageKey = getStorageKey(TEAM_SLUG, dateKey);
  const [alreadySubmitted, setAlreadySubmitted] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem(storageKey) === "true" : false
  );
  const [showThanks, setShowThanks] = useState(false);
  const form = useForm({
    resolver: zodResolver(dailySchema),
    defaultValues: { energy: undefined },
  });

  const onSubmit = async (values) => {
    try {
      await submitDaily(TEAM_SLUG, values.energy);
      toast.success("Daily pulse submitted");
      form.reset({ energy: undefined });
      localStorage.setItem(storageKey, "true");
      setAlreadySubmitted(true);
      setShowThanks(true);
    } catch {
      toast.error("Submission failed");
    }
  };

  const isSubmitting = form.formState.isSubmitting;
  const selected = form.watch("energy");
  const disabled = alreadySubmitted;

  return (
    <div className="mx-auto flex min-h-[60vh] w-full max-w-lg flex-col justify-center px-0 py-8 sm:px-4 sm:py-12">
      <Card className="border-stone-200/50 min-w-0 overflow-hidden bg-card">
        <CardContent className="px-4 py-10 sm:px-8 sm:py-12 md:px-12 md:py-14">
          <p className="text-center text-xs text-stone-400 mb-2">
            {getTodayLabel()}
          </p>
          <h2 className="text-center text-xl font-semibold tracking-tight text-stone-800 leading-snug sm:text-2xl md:text-[1.75rem]">
            How are you feeling today?
          </h2>
          {disabled && (
            <p className="mt-6 text-center text-[0.9375rem] text-stone-500">
              {showThanks ? "Thanks. See you tomorrow." : "Thanks. You’ve checked in for today."}
            </p>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="mt-12 space-y-8">
              <FormField
                control={form.control}
                name="energy"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="space-y-3 sm:space-y-4">
                        <div
                          className="flex items-center justify-between gap-2 sm:justify-evenly sm:gap-4"
                          role="group"
                          aria-label="Feeling scale 1 to 5"
                        >
                          {SCALE.map((value) => {
                            const isSelected = field.value === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                disabled={disabled}
                                onClick={() => field.onChange(value)}
                                className={`
                                  flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-medium sm:h-14 sm:w-14 sm:text-lg
                                  transition-all duration-200 ease-out
                                  focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2
                                  disabled:opacity-60 disabled:pointer-events-none
                                  hover:scale-105 active:scale-95
                                  ${isSelected
                                    ? `${ENERGY_BG_SELECTED[value]} ${ENERGY_TEXT_SELECTED} shadow-soft`
                                    : `${ENERGY_BG[value]} ${ENERGY_TEXT_UNSELECTED} opacity-90 hover:opacity-100`
                                  }
                                `}
                              >
                                {value}
                              </button>
                            );
                          })}
                        </div>
                        <p className="flex justify-between gap-2 px-0 text-xs text-stone-400 tracking-tight sm:px-1 sm:text-[0.8125rem]">
                          <span className="truncate">Low energy</span>
                          <span className="shrink-0">Neutral</span>
                          <span className="truncate text-right">High energy</span>
                        </p>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />

              {!disabled && (
                <Button
                  type="submit"
                  size="lg"
                  className="h-12 w-full text-base font-medium sm:text-[1rem]"
                  disabled={selected == null || isSubmitting}
                >
                  {isSubmitting ? "Submitting…" : "Submit"}
                </Button>
              )}
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
